import { useEffect } from 'react'
import { Input } from '../../../components/Input/Input'
import { ToggleButton } from 'components/Input/ToggleButton'
import {
  Animation,
  Button,
  ButtonsContainer,
  Flex,
  ItemsGrid,
  TabFooter,
  ThinSeparator,
} from '../../../styling/GlobalStyles'
import { CancelBtn } from 'components/Button/CancelBtn'
import { ISelectValue } from 'constants/globalTypes'
import { IItemsTab } from 'types/inventory.interface'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { OtherText, TitleText } from 'styling/Typography'
import { ITEM_NATURE_OPTIONS } from '../Shared/inventoryFormatters'

export const InventoryItemsReplenishmentTab = ({ state, setState, setActive }: IItemsTab) => {
  useEffect(() => {
    if (state?.purchaseUnit || state?.stockUnit || state?.issueUnit) return
    if (state?.unit) {
      setState((prev) => ({
        ...prev,
        purchaseUnit: prev.purchaseUnit ?? prev.unit,
        stockUnit: prev.stockUnit ?? prev.unit,
        issueUnit: prev.issueUnit ?? prev.unit,
        conversionFactor: prev.conversionFactor ?? 1,
      }))
    }
  }, [state?.unit, state?.purchaseUnit, state?.stockUnit, state?.issueUnit, setState])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setState((prev) => ({
      ...prev,
      [name]: name === 'conversionFactor' ? e.target.valueAsNumber : value,
    }))
  }

  const handleSelect = async (value: ISelectValue, name: string) => {
    const copy = structuredClone(state)
    copy[name] = value
    setState(copy)
  }

  const handleNext = () => {
    setActive('Financial')
  }

  return (
    <Animation>
      <Flex column width='100%'>
        <ItemsGrid columns={3} gap='1rem'>
          <HonestSelectAsyncInput
            link='inventory-suppliers'
            name='preferredSupplier'
            labelText='Supplier'
            placeholder='Select Supplier'
            defaultValue={state?.preferredSupplier?.label}
            onChange={handleSelect}
            parserFunction={(data: any) =>
              data.data.map((i: any) => ({ label: i.name, value: i.id }))
            }
            nomarg
          />
          <Input
            value={state?.reorderPoint}
            name='reorderPoint'
            onChange={handleChange}
            type='number'
            placeholder='15'
            labelText='Reorder Point'
          />
          <Input
            value={state?.leadTimeDays}
            name='leadTimeDays'
            onChange={handleChange}
            type='number'
            placeholder='15'
            labelText='Lead Time Days'
          />
          <Input
            value={state?.minLevel}
            name='minLevel'
            onChange={handleChange}
            type='number'
            placeholder='Enter minimum level'
            labelText='Minimum Level'
          />
          <Input
            value={state?.maxLevel}
            name='maxLevel'
            onChange={handleChange}
            type='number'
            placeholder='Enter maximum level'
            labelText='Maximum Level'
          />
          <HonestSelectInput
            name='nature'
            labelText='Nature'
            options={ITEM_NATURE_OPTIONS}
            defaultValue={state?.nature?.value}
            onChange={handleSelect}
            nomarg
          />
          <ToggleButton
            start
            label='Critical item'
            state={Boolean(state?.critical)}
            handleChange={() =>
              setState((prev) => ({ ...prev, critical: !prev.critical }))
            }
          />
        </ItemsGrid>
        <ThinSeparator />
        <Flex column width='100%' gap='0.5rem'>
          <TitleText weight='bold'>Units</TitleText>
          <ItemsGrid columns={4} gap='1rem'>
            <HonestSelectAsyncInput
              link='units'
              name='purchaseUnit'
              labelText='Purchase Unit'
              placeholder='Select unit'
              defaultValue={state?.purchaseUnit?.label}
              onChange={handleSelect}
              parserFunction={(data: any) =>
                data.map((i: any) => ({ label: i.name, value: i.id }))
              }
              nomarg
            />
            <HonestSelectAsyncInput
              link='units'
              name='stockUnit'
              labelText='Stock Unit'
              placeholder='Select unit'
              defaultValue={state?.stockUnit?.label}
              onChange={handleSelect}
              parserFunction={(data: any) =>
                data.map((i: any) => ({ label: i.name, value: i.id }))
              }
              nomarg
            />
            <HonestSelectAsyncInput
              link='units'
              name='issueUnit'
              labelText='Issue Unit'
              placeholder='Select unit'
              defaultValue={state?.issueUnit?.label}
              onChange={handleSelect}
              parserFunction={(data: any) =>
                data.map((i: any) => ({ label: i.name, value: i.id }))
              }
              nomarg
            />
            <Input
              value={state?.conversionFactor}
              name='conversionFactor'
              onChange={handleChange}
              type='number'
              placeholder='1'
              labelText='Conversion Factor'
            />
          </ItemsGrid>
          <OtherText grey>
            {`1 purchase unit = ${state?.conversionFactor || 1} stock units · buy/issue hints show on stock.`}
          </OtherText>
        </Flex>
      </Flex>

      <TabFooter>
        <ButtonsContainer>
          <CancelBtn />
          <Button orange onClick={handleNext}>
            Next
          </Button>
        </ButtonsContainer>
      </TabFooter>
    </Animation>
  )
}
